import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { useAuth } from '../../hooks/useAuth'
import {
  useAccountAppointments,
  useAppointmentAssignees,
  useAppointmentSettings,
  useCancelAppointment,
  useCompleteAppointment,
  useCreateAppointment,
  useNoShowAppointment,
  useRescheduleAppointment,
} from '../../hooks/useAppointments'
import { AppointmentDialog } from '../appointments/AppointmentDialog'
import { AppointmentForm } from '../appointments/AppointmentForm'
import { apiErrorMessage, apiErrorStatus, appointmentScheduleError } from '../../hooks/useErrorHandler'
import { dateTimeLocalToUtcIso, formatAppointmentWhen } from '../../lib/formatAppointmentWhen'
import {
  canAssignAppointment,
  canCreateAppointment,
  canEditAppointment,
  canReadAppointmentSettings,
  emptyAppointmentForm,
  parseAppointmentForm,
  reminderHint,
  reminderLabel,
  statusLabel,
  type AppointmentFormState,
} from '../../lib/appointmentForm'
import type { AccountAnchor, Appointment } from '../../lib/api'

export function AppointmentsPanel({ anchor }: { anchor: AccountAnchor }) {
  const { user } = useAuth()
  const canCreate = canCreateAppointment(user)
  const canAssign = canAssignAppointment(user)
  const canReadSettings = canReadAppointmentSettings(user)
  const appointmentsQuery = useAccountAppointments(anchor)
  const settingsQuery = useAppointmentSettings(canReadSettings)
  const assigneesQuery = useAppointmentAssignees(canAssign)
  const createAppointment = useCreateAppointment()
  const rescheduleAppointment = useRescheduleAppointment(anchor)
  const cancelAppointment = useCancelAppointment(anchor)
  const completeAppointment = useCompleteAppointment(anchor)
  const noShowAppointment = useNoShowAppointment(anchor)

  const [form, setForm] = useState<AppointmentFormState>(emptyAppointmentForm)
  const [error, setError] = useState<string | null>(null)
  const [rescheduleFor, setRescheduleFor] = useState<string | null>(null)
  const [rescheduleAt, setRescheduleAt] = useState('')
  const [editing, setEditing] = useState<Appointment | null>(null)

  if (apiErrorStatus(appointmentsQuery.error) === 403) {
    return null
  }

  const title = anchor.kind === 'merchant' ? 'Merchant appointments' : 'Lead appointments'
  const appointments = appointmentsQuery.data ?? []

  const submit = () => {
    const parsed = parseAppointmentForm(form)
    if ('error' in parsed) {
      setError(parsed.error)
      return
    }
    setError(null)
    createAppointment.mutate(
      { anchor, payload: parsed.payload },
      {
        onSuccess: () => setForm(emptyAppointmentForm()),
        onError: (err) => setError(appointmentScheduleError(anchor.kind, err)),
      },
    )
  }

  const saveReschedule = (appointmentId: string) => {
    if (!rescheduleAt) return
    rescheduleAppointment.mutate(
      { appointmentId, startsAt: dateTimeLocalToUtcIso(rescheduleAt) },
      {
        onSuccess: () => {
          setRescheduleFor(null)
          setRescheduleAt('')
        },
        onError: (err) => setError(apiErrorMessage(err, 'Unable to reschedule.')),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canCreate && (
          <AppointmentForm
            values={form}
            onChange={setForm}
            onSubmit={submit}
            submitLabel="Schedule appointment"
            pending={createAppointment.isPending}
            error={error}
            canAssign={canAssign}
            assignees={assigneesQuery.data ?? []}
            reminderHint={reminderHint(canReadSettings, settingsQuery.data?.reminder_offset_minutes)}
            showLocation
            showNotes
          />
        )}

        {appointmentsQuery.isLoading && <p className="text-sm text-slate-500">Loading appointments...</p>}
        {appointmentsQuery.error && <p className="text-sm text-red-600" role="alert">Unable to load appointments.</p>}
        {!appointmentsQuery.isLoading && appointments.length === 0 && (
          <p className="text-sm text-slate-500">No appointments yet.</p>
        )}

        <ul className="space-y-3">
          {appointments.map((appointment) => (
            <li key={appointment.id} className="rounded-md border border-slate-200 px-3 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold capitalize text-slate-800">
                    {appointment.type} · {statusLabel(appointment.status)}
                  </p>
                  <p className="text-sm text-slate-700">{formatAppointmentWhen(appointment.starts_at)}</p>
                  <p className="text-xs text-slate-500">
                    {appointment.duration_minutes} minutes
                    {appointment.assigned_user ? ` · ${appointment.assigned_user.name}` : ''}
                  </p>
                  <p className="text-xs text-slate-500">{reminderLabel(appointment.reminder_offset_minutes)}</p>
                  {appointment.location && <p className="mt-1 text-sm text-slate-700">{appointment.location}</p>}
                  {appointment.notes && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{appointment.notes}</p>}
                </div>
                {canEditAppointment(user, appointment) && (
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(appointment)}>
                      Edit
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => completeAppointment.mutate(appointment.id)}>
                      Complete
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => noShowAppointment.mutate(appointment.id)}>
                      No-show
                    </Button>
                    <Button type="button" size="sm" variant="secondary" onClick={() => cancelAppointment.mutate(appointment.id)}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setRescheduleFor(appointment.id)
                        setRescheduleAt('')
                      }}
                    >
                      Reschedule
                    </Button>
                  </div>
                )}
              </div>
              {rescheduleFor === appointment.id && canEditAppointment(user, appointment) && (
                <div className="mt-3 flex flex-wrap items-end gap-2">
                  <label className="text-sm text-slate-700">
                    New date and time
                    <input
                      type="datetime-local"
                      value={rescheduleAt}
                      onChange={(event) => setRescheduleAt(event.target.value)}
                      className="mt-1 block rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <Button type="button" size="sm" onClick={() => saveReschedule(appointment.id)} isLoading={rescheduleAppointment.isPending}>
                    Save time
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {editing && (
          <AppointmentDialog
            mode="appointment"
            appointment={editing}
            onClose={() => setEditing(null)}
          />
        )}
      </CardContent>
    </Card>
  )
}
